import { useState, useEffect } from "react";
import { useReportsStore } from "@/store/reportsStore";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import type { Emergency } from "@/types/report";

interface EmergencyFormProps {
  onClose: () => void;
  initialData?: Emergency;
}

export default function EmergencyForm({ onClose, initialData }: EmergencyFormProps) {
  const { addEmergency, updateEmergency } = useReportsStore();

  const isEdit = !!initialData;

  const [name, setName] = useState(initialData?.name || "");
  const [volunteers, setVolunteers] = useState(initialData?.volunteers?.toString() || "");
  const [startDate, setStartDate] = useState(initialData?.startDate || "");
  const [endDate, setEndDate] = useState(initialData?.endDate || "");
  const [history, setHistory] = useState(initialData?.history || "");
  const [region, setRegion] = useState(initialData?.region || "");
  const [status, setStatus] = useState<Emergency["status"]>(initialData?.status || "Продолжается");
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>(initialData?.photoUrls || []);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (initialData?.photoUrls) {
      setPreviews(initialData.photoUrls);
    }
  }, [initialData]);

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setPhotoFiles(prev => [...prev, ...files]);

    const newPreviews: string[] = [...previews];
    let loaded = 0;

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          newPreviews.push(reader.result);
          loaded++;
          if (loaded === files.length) setPreviews(newPreviews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({ title: "Ошибка", description: "Укажите название ЧС", variant: "destructive" });
      return;
    }
    if (!volunteers || isNaN(Number(volunteers)) || Number(volunteers) < 0) {
      toast({ title: "Ошибка", description: "Укажите количество добровольцев", variant: "destructive" });
      return;
    }

    setIsSaving(true);

    const payload: Partial<Omit<Emergency, "id">> & { photoFiles?: File[] } = {
      name: name.trim(),
      volunteers: Number(volunteers),
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      history: history.trim() || undefined,
      region: region.trim() || undefined,
      status,
      photoFiles: photoFiles.length ? photoFiles : undefined,
    };

    try {
      if (isEdit && initialData?.id) {
        await updateEmergency(initialData.id, payload);
      } else {
        await addEmergency(payload as any);
      }
      toast({ title: "Успех", description: isEdit ? "ЧС обновлена" : "ЧС добавлена" });
      onClose(); // Закрываем форму после успеха
    } catch (err: any) {
      console.error("Ошибка сохранения:", err);
      toast({ title: "Ошибка", description: err.message || "Не удалось сохранить", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 py-4">
      <div>
        <Label className="text-purple-200 mb-2 block text-lg font-medium">Название ЧС</Label>
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          className="bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400 py-6"
          disabled={isSaving}
        />
      </div>

      <div>
        <Label className="text-purple-200 mb-2 block text-lg font-medium">Количество добровольцев ВСКС</Label>
        <Input
          type="number"
          value={volunteers}
          onChange={e => setVolunteers(e.target.value)}
          className="bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400 py-6"
          disabled={isSaving}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-purple-200 mb-2 block text-lg font-medium">Дата начала ЧС</Label>
          <Input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="bg-purple-900/50 border-purple-600 text-white py-6"
            disabled={isSaving}
          />
        </div>
        <div>
          <Label className="text-purple-200 mb-2 block text-lg font-medium">Дата окончания ЧС</Label>
          <Input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="bg-purple-900/50 border-purple-600 text-white py-6"
            disabled={isSaving}
          />
        </div>
      </div>

      <div>
        <Label className="text-purple-200 mb-2 block text-lg font-medium">Регион</Label>
        <Input
          placeholder="Например: Краснодарский край, Анапа"
          value={region}
          onChange={e => setRegion(e.target.value)}
          className="bg-purple-900/50 border-purple-600 text-white py-6"
          disabled={isSaving}
        />
      </div>

      <div>
        <Label className="text-purple-200 mb-2 block text-lg font-medium">Статус</Label>
        <Select value={status} onValueChange={v => setStatus(v as any)} disabled={isSaving}>
          <SelectTrigger className="bg-purple-900/50 border-purple-600 text-white py-6">
            <SelectValue placeholder="Выберите статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Продолжается">Продолжается</SelectItem>
            <SelectItem value="Ликвидирована">Ликвидирована</SelectItem>
            <SelectItem value="Восстановление">Восстановление</SelectItem>
            <SelectItem value="Архив">Архив</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-purple-200 mb-2 block text-lg font-medium">История ЧС</Label>
        <Textarea
          value={history}
          onChange={e => setHistory(e.target.value)}
          className="bg-purple-900/50 border-purple-600 text-white min-h-[160px] text-base"
          disabled={isSaving}
        />
      </div>

      <div>
        <Label className="text-purple-200 mb-2 block text-lg font-medium">
          {isEdit ? "Добавить новые фото (старые останутся)" : "Фото (можно несколько)"}
        </Label>
        <Input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFilesChange}
          className="bg-purple-900/50 border-purple-600 text-white file:bg-purple-700 file:text-white file:px-4 file:py-2 file:rounded-md file:border-0"
          disabled={isSaving}
        />

        {previews.length > 0 && (
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {previews.map((src, idx) => (
              <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-purple-600/50 shadow-sm bg-purple-950/30">
                <img src={src} alt={`Превью ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-4 mt-10">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isSaving}
          className="border-purple-600 text-purple-200 hover:bg-purple-900/50 px-8 py-5 text-lg"
        >
          Отмена
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={isSaving}
          className="bg-purple-600 hover:bg-purple-700 px-10 py-5 text-lg min-w-[180px]"
        >
          {isSaving ? "Сохранение..." : (isEdit ? "Сохранить изменения" : "Добавить ЧС")}
        </Button>
      </div>
    </div>
  );
}